/**
 * Utilidades comunes para la aplicación
 */

/** Costo fijo de instalación GPS en pesos mexicanos (alineado con backend GPS_COST_MXN). */
export const GPS_COST_MXN = 3000;

/**
 * Une la base de medios (S3, API, etc.) con una ruta sin generar dobles barras (//).
 * Si path ya es URL absoluta http(s), se devuelve sin cambios.
 * @param {string} base - Base (p. ej. VUE_APP_URL_IMAGE), puede terminar en /
 * @param {string} path - Ruta relativa al bucket/host o ruta absoluta /foo/bar
 * @returns {string}
 */
export function joinMediaBaseUrl(base, path) {
  if (path == null || path === '') return '';
  const p = String(path).trim();
  if (!p) return '';
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  const b = String(base || '').trim().replace(/\/+$/, '');
  const rel = p.replace(/^\/+/, '');
  if (!b) return `/${rel}`;
  return `${b}/${rel}`;
}

/**
 * Función debounce personalizada
 * @param {Function} func - Función a ejecutar
 * @param {number} wait - Tiempo de espera en milisegundos
 * @param {boolean} immediate - Si ejecutar inmediatamente
 * @returns {Function} Función debounced
 */
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Función throttle personalizada
 * @param {Function} func - Función a ejecutar
 * @param {number} limit - Límite de tiempo en milisegundos
 * @returns {Function} Función throttled
 */
export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Formatear número con separadores de miles
 * @param {number} number - Número a formatear
 * @param {string} locale - Locale para formateo
 * @returns {string} Número formateado
 */
export function formatNumber(number, locale = 'es-MX') {
  return new Intl.NumberFormat(locale).format(number || 0);
}

/**
 * Formatear número con exactamente 2 decimales (para monedas)
 * Redondea a número entero primero para evitar problemas de precisión flotante
 * @param {number} number - Número a formatear
 * @param {string} locale - Locale para formateo
 * @returns {string} Número formateado con 2 decimales (.00)
 */
export function formatMoneyNumber(number, locale = 'es-MX') {
  // Redondear a número entero primero para evitar problemas de precisión flotante
  const rounded = Math.round(number || 0);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(rounded);
}

/**
 * Formatear moneda
 * @param {number} amount - Cantidad a formatear
 * @param {string} currency - Moneda (por defecto MXN)
 * @param {string} locale - Locale para formateo
 * @returns {string} Moneda formateada
 */
export function formatCurrency(amount, currency = 'MXN', locale = 'es-MX') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(amount || 0);
}

/**
 * Validar CURP mexicana
 * @param {string} curp - CURP a validar
 * @returns {boolean} True si es válida
 */
export function validateCURP(curp) {
  if (!curp || curp.length !== 18) return false;
  
  const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9]{2}$/;
  return curpRegex.test(curp.toUpperCase());
}

/**
 * Validar RFC mexicano
 * @param {string} rfc - RFC a validar
 * @returns {boolean} True si es válido
 */
export function validateRFC(rfc) {
  if (!rfc || rfc.length < 10 || rfc.length > 13) return false;
  
  const rfcRegex = /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
  return rfcRegex.test(rfc.toUpperCase());
}

/**
 * Validar número de teléfono (internacional).
 * Mínimo 10 dígitos (p. ej. México), máximo 15 (estándar E.164).
 * @param {string} phone - Teléfono a validar (puede incluir código de país y caracteres)
 * @returns {boolean} True si es válido
 */
export function validatePhone(phone) {
  if (!phone) return false;
  const cleanPhone = String(phone).replace(/\D/g, '');
  return cleanPhone.length >= 10 && cleanPhone.length <= 15;
}

/**
 * Validar email
 * @param {string} email - Email a validar
 * @returns {boolean} True si es válido
 */
export function validateEmail(email) {
  if (!email) return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validar placa: máximo 7 caracteres, solo letras y números en cualquier orden.
 * @param {string} plate - Placa a validar
 * @returns {boolean} True si es válida
 */
export function validatePlate(plate) {
  if (!plate || typeof plate !== 'string') return false;
  const trimmed = plate.trim();
  if (trimmed.length > 7) return false;
  return /^[A-Za-z0-9]*$/.test(trimmed);
}

/**
 * Validar VIN
 * @param {string} vin - VIN a validar
 * @returns {boolean} True si es válido
 */
export function validateVIN(vin) {
  if (!vin || vin.length !== 17) return false;
  
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
  return vinRegex.test(vin.toUpperCase());
}

/**
 * Generar ID único
 * @returns {string} ID único
 */
export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * Limpiar string para búsqueda
 * @param {string} str - String a limpiar
 * @returns {string} String limpio
 */
export function cleanSearchString(str) {
  if (!str) return '';
  return str.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remover acentos
    .trim();
}

/**
 * Capitalizar primera letra
 * @param {string} str - String a capitalizar
 * @returns {string} String capitalizado
 */
export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Capitalizar cada palabra
 * @param {string} str - String a capitalizar
 * @returns {string} String capitalizado
 */
export function capitalizeWords(str) {
  if (!str) return '';
  return str.split(' ')
    .map(word => capitalize(word))
    .join(' ');
}

/**
 * Obtener iniciales de un nombre
 * @param {string} firstName - Primer nombre
 * @param {string} lastName - Apellido
 * @returns {string} Iniciales
 */
export function getInitials(firstName, lastName) {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last;
}

/**
 * Parsear una fecha de forma segura.
 * Las fechas puras YYYY-MM-DD se tratan como fecha local para evitar desfases por UTC.
 * @param {Date|string} date - Fecha a parsear
 * @returns {Date|null} Fecha parseada o null si no es válida
 */
export function parseDateValue(date) {
  if (!date) return null;
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  const rawDate = String(date).trim();
  const dateOnlyMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const dateObj = new Date(rawDate);
  return isNaN(dateObj.getTime()) ? null : dateObj;
}

/**
 * Formatear fecha
 * @param {Date|string} date - Fecha a formatear
 * @param {string} locale - Locale para formateo
 * @returns {string} Fecha formateada
 */
export function formatDate(date, locale = 'es-MX') {
  if (!date) return '';

  const dateObj = parseDateValue(date);
  if (!dateObj) return '';

  return new Intl.DateTimeFormat(locale).format(dateObj);
}

/**
 * Formatear fecha y hora
 * @param {Date|string} date - Fecha a formatear
 * @param {string} locale - Locale para formateo
 * @returns {string} Fecha y hora formateada
 */
export function formatDateTime(date, locale = 'es-MX') {
  if (!date) return '';

  const dateObj = parseDateValue(date);
  if (!dateObj) return '';

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
}

/**
 * Calcular edad
 * @param {Date|string} birthDate - Fecha de nacimiento
 * @returns {number} Edad en años
 */
export function calculateAge(birthDate) {
  if (!birthDate) return 0;
  
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Verificar si es un archivo de imagen válido
 * @param {File} file - Archivo a verificar
 * @returns {boolean} True si es imagen válida
 */
export function isValidImageFile(file) {
  if (!file) return false;
  
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB
  
  return validTypes.includes(file.type) && file.size <= maxSize;
}

/**
 * Convertir archivo a base64
 * @param {File} file - Archivo a convertir
 * @returns {Promise<string>} Base64 string
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

/**
 * Descargar archivo
 * @param {string} data - Datos del archivo
 * @param {string} filename - Nombre del archivo
 * @param {string} type - Tipo MIME
 */
export function downloadFile(data, filename, type = 'application/octet-stream') {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** MazPhoneNumberInput: country-code = ISO (MX, US, CU); v-model = E.164 (+52...) */
const ISO_TO_DIAL = { MX: '52', US: '1', CU: '53' };

/**
 * Separa lo que devuelve Maz para guardar en API: mainCodePhone = ISO, mainPhone = solo nacional.
 */
export function parseMazPhoneForApi(mainPhone, mainCodePhone) {
  const iso = String(mainCodePhone || 'MX')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '') || 'MX';
  let digits = String(mainPhone || '').replace(/\D/g, '');
  const dial = ISO_TO_DIAL[iso];
  if (dial && digits.startsWith(dial)) {
    digits = digits.slice(dial.length);
  }
  if (digits.length > 10 && digits.startsWith('52')) {
    digits = digits.slice(-10);
  } else if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  } else if (iso === 'CU' && digits.length > 8 && digits.startsWith('53')) {
    digits = digits.slice(-8);
  }
  return { mainPhone: digits, mainCodePhone: iso };
}

/**
 * Valores para enlazar Maz al editar (BD guarda nacional + ISO).
 */
export function phoneFieldsForMaz(mainPhone, mainCodePhone) {
  const { mainPhone: national, mainCodePhone: iso } = parseMazPhoneForApi(
    mainPhone,
    mainCodePhone
  );
  if (!national) {
    return { mainPhone: '', mainCodePhone: iso };
  }
  const dial = ISO_TO_DIAL[iso] || '52';
  return {
    mainPhone: `+${dial}${national}`,
    mainCodePhone: iso,
  };
}
